/**
 * StateAdapter - Handles state conversion between legacy react-table and TanStack Table
 * Manages filters, sorting, and pagination state mapping
 */
import { 
  ColumnFiltersState, 
  SortingState, 
  PaginationState 
} from "@tanstack/react-table";
import { LegacyFilter } from "./ColumnAdapter";

export class StateAdapter {
  /**
   * Converts legacy Filter[] to TanStack ColumnFiltersState
   */
  static mapFiltersToTanStack(legacyFilters: LegacyFilter[]): ColumnFiltersState {
    if (!legacyFilters || legacyFilters.length === 0) {
      return [];
    }

    return legacyFilters.map(filter => ({
      id: filter.id,
      value: filter.value,
    }));
  }

  /**
   * Converts TanStack ColumnFiltersState to legacy Filter[]
   */
  static mapFiltersFromTanStack(tanstackFilters: ColumnFiltersState): LegacyFilter[] {
    if (!tanstackFilters || tanstackFilters.length === 0) {
      return [];
    }

    return tanstackFilters.map(filter => ({
      id: filter.id,
      value: String(filter.value),
    }));
  }

  /**
   * Creates initial pagination state matching legacy behavior
   */
  static createInitialPagination(
    pageSize: number,
    startingPage?: number
  ): PaginationState {
    return {
      pageIndex: startingPage || 0,
      pageSize: pageSize,
    };
  }

  /**
   * Creates initial sorting state (empty by default)
   */
  static createInitialSorting(): SortingState {
    return [];
  }

  /**
   * Creates initial column filters state
   */
  static createInitialFilters(initialFilters?: LegacyFilter[]): ColumnFiltersState {
    if (!initialFilters) {
      return [];
    }
    return this.mapFiltersToTanStack(initialFilters);
  }

  /**
   * Synchronizes legacy filter prop changes with TanStack state
   */
  static syncFilters(
    legacyFiltered: LegacyFilter[] | undefined,
    currentFilters: ColumnFiltersState,
    setFilters: (filters: ColumnFiltersState) => void
  ): void {
    if (!legacyFiltered) return;

    const newFilters = this.mapFiltersToTanStack(legacyFiltered);
    
    // Only update if filters actually changed
    if (!this.areFiltersEqual(currentFilters, newFilters)) {
      setFilters(newFilters);
    }
  }

  /**
   * Compares two filter arrays for equality
   */
  private static areFiltersEqual(
    filters1: ColumnFiltersState,
    filters2: ColumnFiltersState
  ): boolean {
    if (filters1.length !== filters2.length) return false;

    return filters1.every((filter1, index) => {
      const filter2 = filters2[index];
      return filter1.id === filter2.id && filter1.value === filter2.value;
    });
  }

  /**
   * Handles pagination changes and notifies legacy handlers
   */
  static handlePaginationChange(
    newPagination: PaginationState,
    onPageChange?: (page: number) => void
  ): void {
    if (onPageChange) {
      onPageChange(newPagination.pageIndex);
    }
  }

  /**
   * Resets pagination to first page when filters change
   */
  static resetPaginationOnFilter(
    setPagination: (pagination: PaginationState | ((prev: PaginationState) => PaginationState)) => void
  ): void {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }
}